import React, { createContext, useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import {
  ApolloClient,
  ApolloError,
  NetworkStatus,
  useLazyQuery,
  useQuery,
} from '@apollo/client';
import { v4 as uuidv4 } from 'uuid';
import {
  getUser,
  getUser_getUserV2_team_channels,
  getUser_getUserV2_team_users,
  getUser_getUserV2,
} from 'whatis_gql/src/queries/__generated__/getUser';
import {
  getMinimalUser,
  getMinimalUser_getUserV2,
} from 'whatis_gql/src/queries/__generated__/getMinimalUser';
import { getMinimalUserQuery, getUserQuery } from 'whatis_gql/src/queries/User';
import { loginExtensionUser, logoutExtensionUser } from './extensionUtil';
import jwt_decode from 'jwt-decode';
import { useQuery as urlQueryParams } from '../../hooks/useQuery';
import { useNavigate } from 'react-router-dom';
import { useSegment } from '../../hooks/segment';

export interface JWTAuthResponse {
  user_id: string;
  team_id: string;
  access_token: string;
  team_name?: string;
  team_uuid?: string;
  user_uuid?: string;
  iat: number;
  user?: {
    name: string;
    profile_img: string;
  };
}

export interface filterableUser {
  team: {
    users: getUser_getUserV2_team_users[];
    bot_user_id: string;
  };
}

export const filterTeamUsers = (
  user: filterableUser | undefined
): getUser_getUserV2_team_users[] => {
  const users =
    user?.team.users.slice().filter((u) => u.user_id !== 'USLACKBOT') || [];
  return users;
};

export interface AuthProviderType {
  canDoAdminThings: boolean;
  channels: getUser_getUserV2_team_channels[];
  fullUserIsLoading: boolean;
  isExternalUser: boolean;
  minimalUser: getMinimalUser_getUserV2 | undefined;
  minimalUserIsLoading: boolean;
  storedJWT: JWTAuthResponse | undefined;
  user?: getUser_getUserV2;
  userIsWhatisAdmin?: boolean;
  userIsWorkspaceAdmin?: boolean;
  whatisAdmins: getUser_getUserV2_team_users[];
  whatisUsers: getUser_getUserV2_team_users[];
  getChannelByUUID: (
    channel_uuid: string
  ) => getUser_getUserV2_team_channels | undefined;
  usersAndChannels: (
    | getUser_getUserV2_team_users
    | getUser_getUserV2_team_channels
  )[];
  loginUser: (jwt: string) => void;
  logoutUser: () => void;
  reloadUser: () => void;
  getNonAdmins: (excludeSelf: boolean) => getUser_getUserV2_team_users[];
  userIsRestricted: boolean;
  backendDown: boolean;
}

export const AuthContext = createContext<AuthProviderType>({
  canDoAdminThings: false,
  channels: [],
  fullUserIsLoading: true,
  isExternalUser: false,
  storedJWT: undefined,
  minimalUser: undefined,
  minimalUserIsLoading: true,
  usersAndChannels: [],
  whatisAdmins: [],
  whatisUsers: [],
  getNonAdmins: (excludeSelf: boolean) => [],
  getChannelByUUID: (channel_uuid: string) => undefined,
  loginUser: (jwt: string) => undefined,
  logoutUser: () => undefined,
  reloadUser: () => undefined,
  userIsRestricted: false,
  backendDown: false,
});

interface AuthProviderProps {
  children: React.ReactNode;
  client: ApolloClient<any>;
}

const AuthProvider = ({ children, client }: AuthProviderProps) => {
  const storedToken = localStorage.getItem('token');
  const segment = useSegment();
  let storedJwt = undefined;
  try {
    storedJwt = storedToken
      ? (jwt_decode(storedToken) as JWTAuthResponse)
      : undefined;
  } catch (e) {
    console.error('Error decoding jwt: ', e);
    Sentry.captureException(e);
  }

  const logoutUser = async (redirect = true) => {
    setBackendDown(false);
    localStorage.removeItem('token');
    setUser(undefined);
    setJwtToken(undefined);
    segment?.setAnonymousId(uuidv4());
    await client.resetStore();
    if (redirect) navigate('/');
    logoutExtensionUser();
  };

  const [token, setToken] = useState<string>(storedToken || '');
  const [jwtToken, setJwtToken] = useState<JWTAuthResponse | undefined>(
    storedJwt
  );

  const [user, setUser] = useState<getUser_getUserV2 | undefined>(undefined);
  const navigate = useNavigate();
  const query = urlQueryParams();
  const [backendDown, setBackendDown] = useState(false);

  const onQueryError = (err: ApolloError) => {
    if (
      err.graphQLErrors.length > 0 &&
      err.graphQLErrors[0].extensions &&
      err.graphQLErrors[0].extensions.code === 'not_authenticated'
    ) {
      logoutUser(false);
    } else {
      Sentry.captureException(err);
    }

    const to = query.requested_page || window.location.pathname.toString();
    if (err.message === 'Failed to fetch') {
      // the backend is down, just return
      setBackendDown(true);
      return;
    }

    navigate('/login?requested_page=' + to);
  };

  const [fetchUser, { loading, data, networkStatus }] = useLazyQuery<getUser>(
    getUserQuery,
    {
      onError: onQueryError,
    }
  );

  useEffect(() => {
    setUser(data?.getUserV2);
  }, [data]);

  const { loading: minimalUserIsLoading, data: minimalUser } =
    useQuery<getMinimalUser>(getMinimalUserQuery, {
      skip: token === '' || (!jwtToken?.user_uuid && !jwtToken?.team_uuid),
      onError: onQueryError,
      onCompleted: () => {
        setBackendDown(false);
        fetchUser();
      },
    });

  useEffect(() => {
    if (!loading && data?.getUserV2.__typename === 'WhatisUser') {
      onUserLoaded(data.getUserV2);
    }
  }, [data, loading, networkStatus]);

  if (storedJwt && !storedJwt.team_name) {
    logoutUser(true);
  }

  const onUserLoaded = (user: getUser_getUserV2) => {
    let id;
    if (user.team.team_id) {
      id = `${user.team.team_id}-${user.user_id}`;
    } else if (user.uuid) {
      id = user.uuid;
    }

    segment?.identify(id, {
      email: user.email,
    });
    segment?.alias(id, user.user_id!);
    segment?.alias(id, user.uuid!);

    Sentry.setTag('user_id', user?.user_id);
    Sentry.setTag('user_uuid', user?.uuid);
    Sentry.setTag('team_id', user?.team.team_id);
    Sentry.setTag('team_uuid', user?.team.uuid);
    // log user into chrome extension
    loginExtensionUser(user, token);
  };

  const getWhatisAdmins = () => {
    return user?.team.users.filter((u) => u.is_whatis_admin) || [];
  };

  const getNonAdmins = (excludeSelf = false) => {
    return filterTeamUsers(user as filterableUser).filter((u) => {
      if (
        excludeSelf &&
        u.uuid === user?.uuid &&
        (user?.is_whatis_admin || user?.is_workspace_admin)
      ) {
        return false;
      }

      return (
        user?.team.users.filter(
          (u) => !u.is_whatis_admin && !u.is_workspace_admin
        ) || []
      );
    });
  };

  const getChannelByUUID = (channel_uuid: string) => {
    return user?.team.channels?.find((c) => {
      return c.uuid === channel_uuid;
    });
  };

  const loginUser = (token: string) => {
    localStorage.setItem('token', token);
    setToken(token);
    const decoded = jwt_decode(token) as JWTAuthResponse;
    setJwtToken(decoded);
  };

  const getUserIsExternal = (): boolean => {
    return (
      user?.is_restricted ||
      user?.is_ultra_restricted ||
      user?.is_stranger ||
      false
    );
  };

  const usersAndChannels = () => {
    let res: (
      | getUser_getUserV2_team_users
      | getUser_getUserV2_team_channels
    )[] = [];
    res = res.concat(filterTeamUsers(user as filterableUser));
    res = res.concat([...(user?.team.channels || [])]);
    return res;
  };

  return (
    <AuthContext.Provider
      value={{
        backendDown,
        canDoAdminThings:
          minimalUser?.getUserV2.is_workspace_admin ||
          minimalUser?.getUserV2.is_whatis_admin ||
          false,
        channels: user?.team.channels || [],
        fullUserIsLoading:
          token != '' &&
          (minimalUserIsLoading ||
            loading ||
            networkStatus === NetworkStatus.refetch) &&
          user === undefined,
        getChannelByUUID,
        getNonAdmins,
        isExternalUser: getUserIsExternal(),
        loginUser,
        logoutUser,
        minimalUser: minimalUser?.getUserV2,
        minimalUserIsLoading,
        reloadUser: fetchUser,
        storedJWT: jwtToken,
        user: user,
        userIsWhatisAdmin: user?.is_whatis_admin,
        userIsWorkspaceAdmin: user?.is_workspace_admin,
        usersAndChannels: usersAndChannels(),
        whatisAdmins: getWhatisAdmins(),
        whatisUsers: filterTeamUsers(user as filterableUser),
        userIsRestricted:
          !!user?.team.restricted_mode && !user?.is_whatis_admin,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;

export const useAuth = () => React.useContext(AuthContext);
